'use client';

import { ChakraProvider } from '@chakra-ui/react';
import { IS_STORYBOOK } from '@m/config/FeatureFlags';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { ColorModeProvider, type ColorModeProviderProps } from './color-mode';
import { DesignThemeProvider, useDesignTheme } from './design-theme';
import { getTheme, type ThemeName } from './themes/registry';
import { Toaster } from './toaster';

export const metropolitanQueryClient = new QueryClient();

export interface ProviderProps extends ColorModeProviderProps {
  /** Uncontrolled default design theme when `themeName` is omitted. */
  defaultThemeName?: ThemeName;
  /** Force a design theme for this tree (like next-themes `forcedTheme` for color mode). */
  forcedThemeName?: ThemeName;
  /** Called when the design theme changes (controlled or uncontrolled). */
  onThemeChange?: (theme: ThemeName) => void;
  /**
   * Controlled design theme name (`default` | `luxury`).
   * Independent of light/dark color mode (`defaultTheme` / `forcedTheme` from next-themes).
   */
  themeName?: ThemeName;
}

function ChakraSystemProvider({ children, ...colorModeProps }: ColorModeProviderProps) {
  const { theme } = useDesignTheme();
  const system = getTheme(theme);

  return (
    <ColorModeProvider {...colorModeProps}>
      <ChakraProvider value={system}>
        {children}
        <Toaster />
      </ChakraProvider>
    </ColorModeProvider>
  );
}

export function Provider({
  themeName,
  defaultThemeName = 'default',
  forcedThemeName,
  onThemeChange,
  children,
  ...colorModeProps
}: ProviderProps) {
  return (
    <QueryClientProvider client={metropolitanQueryClient}>
      {!IS_STORYBOOK && <ReactQueryDevtools initialIsOpen={false} />}
      <DesignThemeProvider
        theme={themeName}
        defaultTheme={defaultThemeName}
        forcedTheme={forcedThemeName}
        onThemeChange={onThemeChange}
      >
        <ChakraSystemProvider {...colorModeProps}>{children}</ChakraSystemProvider>
      </DesignThemeProvider>
    </QueryClientProvider>
  );
}
